import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  LinkAnnotationService, BookmarkViewService, MagnificationService,
  ThumbnailViewService, ToolbarService, NavigationService, SignatureFieldSettings,
  AnnotationService, TextSearchService, TextSelectionService, PdfFormFieldBaseModel,
  PrintService, FormDesignerService, FormFieldsService, TextFieldSettings, LoadEventArgs, UnloadEventArgs, DownloadStartEventArgs, DownloadEndEventArgs, PrintStartEventArgs, PrintEndEventArgs
} from '@syncfusion/ej2-angular-pdfviewer';

@Component({
  selector: 'app-pdf-viewer',
  templateUrl: './pdf-viewer.component.html',
  styleUrls: ['./pdf-viewer.component.css'],
  providers: [LinkAnnotationService, BookmarkViewService, MagnificationService,
    ThumbnailViewService, ToolbarService, NavigationService, SignatureFieldSettings,
    AnnotationService, TextSearchService, TextSelectionService,
    PrintService, FormDesignerService, FormFieldsService, TextFieldSettings]
})

export class HRIMPdfViewerComponent implements OnInit {
  @Input() pdfDirtyState: boolean;
  @Input() enableFormFields: boolean;
  @Input() enablePrint: boolean;
  @Input() doStylingCondition: boolean;
  @Input() enableDownload: boolean;
  @Input() allowAnnotationEditTool: boolean;
  @Input() hideCommentUndoRedo: boolean;
  printMode: string = "NewWindow";
  pdfResource: string;
  pdfService: string;
  private originalGetSignatureBackgroundFn: (color: string) => string = (x) => x;
  private originalFieldStyles: Record<string, TextFieldSettings> = {};

  constructor() { }

  ngOnInit(): void {
    this.pdfService = this.enableFormFields ? "/Api/PdfViewer" : null;
    this.pdfResource = this.enableFormFields ? null : window.location.origin + "/dist/assets/ej2-pdfviewer-lib";
  }

  getPdfViewerControl(): any {
    let viewer = document.getElementById('HrimPdfViewer');
    let instances = (<any>viewer).ej2_instances;
    return instances[0];
  }

  public documentLoaded(e: LoadEventArgs): void {
    this.storeOriginalFieldStyles();
    this.handleFormFieldStyling();
  }

  public documentStartDownload(args: DownloadStartEventArgs): void {
    const pdfViewer = this.getPdfViewerControl();
    pdfViewer.enableFormFieldsValidation = false;
  };

  public documentEndDownload(args: DownloadEndEventArgs): void {
    const pdfViewer = this.getPdfViewerControl();
    pdfViewer.enableFormFieldsValidation = true;
  };

  public documentStartPrint(args: PrintStartEventArgs): void {
    const pdfViewer = this.getPdfViewerControl();
    pdfViewer.enableFormFieldsValidation = false;
  };

  public documentEndPrint(args: PrintEndEventArgs): void {
    const pdfViewer = this.getPdfViewerControl();
    pdfViewer.enableFormFieldsValidation = true;
  };

  public handleFormFieldStyling(): void {
    if (this.doStylingCondition) {
      this.fixSyncFusionBackgroundColorLogic();
      this.updateDateFieldInputTypesToUseDateInput();
      this.useNormalStylesOnAllFields();
    }
  }

  public setPDFData(pdfData: string, pdfFileName: string): void {
    let pdfViewer = this.getPdfViewerControl();
    pdfViewer.toolbar.showToolbarItem(new Array("SubmitForm"), false);
    pdfViewer.toolbar.showToolbarItem(new Array("OpenOption"), false);
    pdfViewer.toolbar.showToolbarItem(new Array("AnnotationEditTool"), this.allowAnnotationEditTool);
    pdfViewer.toolbar.showToolbarItem(new Array("PrintOption"), true);
    pdfViewer.toolbar.showToolbarItem(new Array("DownloadOption"), this.enableDownload);
    if (!this.enablePrint) {
      pdfViewer.toolbar.showToolbarItem(new Array("PrintOption"), false);
      pdfViewer.enablePrint = false;
    }
    pdfViewer.toolbar.showToolbarItem(new Array("FormDesignerEditTool"), false);
    if (this.hideCommentUndoRedo) {
      pdfViewer.toolbar.showToolbarItem(new Array("CommentTool"), false);
      pdfViewer.toolbar.showToolbarItem(new Array("UndoRedoTool"), false);
    }
    pdfViewer.documentPath = "data:application/pdf;base64," + pdfData;
    pdfViewer.downloadFileName = pdfFileName;
  }

  public async getPDFData(): Promise<string> {
    let pdfViewer = this.getPdfViewerControl();
    let blobResult = await pdfViewer.saveAsBlob();
    let base64data = await this.readAsync(blobResult);
    return base64data.split(";")[1].split(",")[1]
  }

  private readAsync(blob: Blob) {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        resolve(e.target.result.toString());
      };
      reader.onerror = () => {
        reject(new Error('Error reading pdf data from viewer'))
      };
      reader.readAsDataURL(blob);
    });
  }

  public invokePdfDownload(): any {
    this.getPdfViewerControl().download();
  }

  public validatePdfFields(): string[] {
    let pdfViewer = this.getPdfViewerControl();
    var allRequiredFields = pdfViewer.retrieveFormFields();
    var requiredFieldsWithNoValue = this.getRequiredFieldsWithNoValue(allRequiredFields);
    var existingFields = {};
    const requiredFields = pdfViewer.formFields.filter(x => x.isRequired && !x.isReadOnly);
      requiredFields.forEach(y => {
        existingFields[y.id] = true;
      });

    allRequiredFields.filter(i => i.isRequired && !i.isReadOnly).forEach(x => {
      if (x.type != "Checkbox" && x.type != "DropdownList") {
        let hasValue = !!x.value;
        if (x.type === PdfFieldType.RadioButton) {
          hasValue = allRequiredFields.filter(field => field.name === x.name).some(field => field.isSelected);
        }
        if (hasValue) {
          if (existingFields[x.id])
            this.applyFieldStyles(x.id as string, { backgroundColor: "#daeaf7" });
        } else {
          if (existingFields[x.id])
            this.applyFieldStyles(x.id as string, { backgroundColor: "#ff000060" });

          if (!requiredFieldsWithNoValue.some(field => field.name === x.name)) {
            requiredFieldsWithNoValue.push(x);
          }
        }
      } else if (x.type == "Checkbox") {
        if (!x.isChecked) {
          if (existingFields[x.id])
            this.applyFieldStyles(x.id as string, { backgroundColor: "#ff000060" });

          if (!requiredFieldsWithNoValue.some(field => field.name === x.name)) {
            requiredFieldsWithNoValue.push(x);
          }
        } else {
          if (existingFields[x.id])
            this.applyFieldStyles(x.id as string, { backgroundColor: "#daeaf7" });
        }
      } else if (x.type == "DropdownList") {
        if (!x.selectedIndex) {
          if (existingFields[x.id])
            this.applyFieldStyles(x.id as string, { backgroundColor: "#ff000060" });

          if (!requiredFieldsWithNoValue.some(field => field.name === x.name)) {
            requiredFieldsWithNoValue.push(x);
          }
        } else {
          if (existingFields[x.id])
            this.applyFieldStyles(x.id as string, { backgroundColor: "#daeaf7" });
        }
      }
    });

    const NamesOfRequiredFieldsWithNoValue = Array.from(new Set(requiredFieldsWithNoValue.map(field => field.name)));

    return NamesOfRequiredFieldsWithNoValue;
  }

  private getRequiredFieldsWithNoValue(requiredFields) {
    const radioGroups = {};
    let requiredFieldsWithNoValue = [];

    requiredFields.forEach(field => {
      if (((["Textbox", "Password", "InitialField", "SignatureField"].includes(field.type) && field.value == "")
        || (field.type == "Checkbox" && field.isChecked == false)) && field.isRequired && !field.isReadOnly) {
        requiredFieldsWithNoValue.push(field)
      } else if (field.type == "RadioButton" && field.isRequired && !field.isReadOnly) {
        radioGroups[field.name] = radioGroups[field.name] ? radioGroups[field.name] : [];
        radioGroups[field.name].push(field);
      }
    })

    Object.keys(radioGroups).forEach(group => {
      let isSelected = false;
      radioGroups[group].forEach(radioButton => {
        if (radioButton.isSelected) {
          isSelected = true;
        }
      })
      if (!isSelected) {
        requiredFieldsWithNoValue = requiredFieldsWithNoValue.concat(radioGroups[group])
      }
    })

    return requiredFieldsWithNoValue;
  }

  private fixSyncFusionBackgroundColorLogic() {
    let pdfViewer = this.getPdfViewerControl();
    this.originalGetSignatureBackgroundFn = pdfViewer.formDesigner.getSignatureBackground;
    pdfViewer.formDesigner.getSignatureBackground = x => x;
  }

  private storeOriginalFieldStyles() {
    let pdfViewer = this.getPdfViewerControl();
    pdfViewer.formFieldCollection.forEach((field: PdfFormFieldBaseModel) => {
      if (!field.id) return;
      // Copy the existing styles (important to use a copy and not original object as that will change)
      const pdfFieldStyles: TextFieldSettings = Object.assign({}, this.getFieldStyles(field.id));
      this.originalFieldStyles[field.id] = pdfFieldStyles;
    });
  }

  private updateDateFieldInputTypesToUseDateInput() {
    let pdfViewer = this.getPdfViewerControl();
    const dateFields = pdfViewer.formFields.filter(x => (x.name ?? '').toLowerCase().indexOf("datepicker") >= 0 && !x.isMultiline);
    dateFields.forEach(x => {
      (window.document.getElementById(x.id!) as HTMLInputElement).type = HtmlInputType.date;
    });
  }

  private useNormalStylesOnAllFields(): void {
    let pdfViewer = this.getPdfViewerControl();
    pdfViewer.formFieldCollection.forEach(field => {
      const updatedStyles = { backgroundColor: "#daeaf7" } as TextFieldSettings;
      if (field.isRequired && !field.isReadOnly) {
        updatedStyles.borderColor = "#ff0000";
      }
      this.applyFieldStyles(field.id, updatedStyles);
    });
  }

  private getFieldStyles(fieldId: string): TextFieldSettings {
    let pdfViewer = this.getPdfViewerControl();
    const field = pdfViewer.formDesignerModule.getFormField(fieldId);
    // Getting background color is tricky b/c SyncFusion alters the Hex background color alpha component to always be hex "60".
    // This code uses the full opacity for background color, which is hex "ff".
    let backgroundColor = field.backgroundColor;
    const regexHexWithAlpha = /^(#[0-9A-Fa-f]{6})(60)$/
    const backgroundColorMatch = backgroundColor?.match(regexHexWithAlpha);
    if (backgroundColorMatch) {
      const [_fullBgColorStr, bgNoAlpha] = backgroundColorMatch;
      backgroundColor = `${bgNoAlpha}ff`;
    }
    return {
      alignment: field.alignment,
      backgroundColor,
      borderColor: field.borderColor,
      color: field.color,
      font: field.font,
      fontFamily: field.fontFamily,
      fontSize: field.fontSize,
      fontStyle: field.fontStyle,
      opacity: field.opacity,
      rotateAngle: field.rotateAngle,
      signatureIndicatorSettings: field.signatureIndicatorSettings,
      thickness: field.thickness,
      visibility: field.visibility,
      zIndex: field.zIndex,
    } as unknown as TextFieldSettings
  }

  private applyFieldStyles(fieldId: string, newStyles: Partial<TextFieldSettings>) {
    let pdfViewer = this.getPdfViewerControl();
    const field = pdfViewer.formDesigner.getFormField(fieldId);
    const fieldStyles = this.getFieldStyles(fieldId);
    const styles: TextFieldSettings = Object.assign({}, fieldStyles, newStyles as TextFieldSettings);
    if (field.formFieldAnnotationType === PdfFieldType.Signature) {
      styles.backgroundColor = this.originalGetSignatureBackgroundFn(styles.backgroundColor);
    }
    pdfViewer.formDesignerModule.updateFormField(fieldId, styles);
  }

  public restoreOriginalFieldStyles() {
    var pdfviewer = this.getPdfViewerControl();
    pdfviewer.formFieldCollection.forEach(field => {
      const fieldStyles = this.originalFieldStyles[field.id];
      if (!fieldStyles) return;
      this.applyFieldStyles(field.id, fieldStyles);
    });
  }
}

enum PdfFieldType {
  Textbox = "Textbox",
  Signature = "SignatureField",
  RadioButton = "RadioButton",
}

enum HtmlInputType {
  text = "text",
  date = "date",
}